import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { toast } from "react-toastify";
import { apiService } from '../../../services/api';
import {
  FormaPagamento,
  CreateFormaPagamentoData,
  UpdateFormaPagamentoData,
  FormaPagamentoFilters
} from '../../../types/financeiro';
import ScrollableTable from '../contracts/components/ScrollableTable';

const FormasPagamento = () => {
  // Estados principais
  const [formas, setFormas] = useState<FormaPagamento[]>([]);
  const [filteredFormas, setFilteredFormas] = useState<FormaPagamento[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Estados para formulários
  const [formaSelecionada, setFormaSelecionada] = useState<FormaPagamento | null>(null);
  const [formData, setFormData] = useState<CreateFormaPagamentoData>({
    nome: '',
    descricao: '',
    ativo: true,
    padrao: false
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateFormaPagamentoData>({});

  // Estados para controle
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Estados para controle de erros nos modais
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Função para fechar modal programaticamente
  const closeModal = (modalId: string) => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      // Remove classes do Bootstrap
      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');

      // Remove backdrop
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }

      // Remove classe do body
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }
  };

  // Buscar formas de pagamento
  const fetchFormas = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getFormasPagamento();

      if (response.success && response.data) {
        let formasArray: FormaPagamento[] = [];

        if (Array.isArray(response.data)) {
          formasArray = response.data as FormaPagamento[];
        } else {
          const responseData = response.data as any;
          formasArray = responseData.data || [];
        }

        const formasComKey = formasArray.map((forma: any) => ({
          ...forma,
          key: forma.id.toString()
        }));

        setFormas(formasComKey);
        setFilteredFormas(formasComKey);
      } else {
        console.error('Resposta sem sucesso ou sem dados:', response);
        setError('Não foi possível obter os dados das formas de pagamento');
      }
    } catch (err) {
      console.error('Erro ao buscar formas de pagamento:', err);
      setError('Erro ao carregar dados das formas de pagamento');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFormas();
  }, []);

  // Filtro de pesquisa com debounce
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredFormas(formas);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = formas.filter(forma => {
        const searchableFields = [
          forma.nome,
          forma.descricao,
          forma.ativo ? 'Ativo' : 'Inativo',
          forma.padrao ? 'Padrão' : 'Não padrão'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredFormas(filtered);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, formas]);

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para editar forma de pagamento
  const handleEditForma = async (id: number) => {
    try {
      setEditError(null);
      const forma = formas.find(c => c.id === id);
      if (forma) {
        setFormaSelecionada(forma);
        setUpdateFormData({
          nome: forma.nome,
          descricao: forma.descricao,
          ativo: forma.ativo,
          padrao: forma.padrao
        });
      }
    } catch (err) {
      console.error('Erro ao preparar edição:', err);
      setEditError('Erro ao carregar dados da forma de pagamento');
    }
  };

  // Função para submeter edição
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formaSelecionada) return;

    try {
      setIsLoading(true);
      setEditError(null);

      const response = await apiService.updateFormaPagamento(formaSelecionada.id, updateFormData);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_edit .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        await fetchFormas();
        toast.success('Forma de pagamento atualizada com sucesso!');
      } else {
        setEditError('Erro ao atualizar forma de pagamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao atualizar forma de pagamento:', err);
      setEditError('Erro ao atualizar forma de pagamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para criar forma de pagamento
  const handleCreateForma = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      const response = await apiService.createFormaPagamento(formData);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_add .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        // Resetar formulário
        setFormData({
          nome: '',
          descricao: '',
          ativo: true,
          padrao: false
        });

        await fetchFormas();
        toast.success('Forma de pagamento criada com sucesso!');
      } else {
        setError('Erro ao criar forma de pagamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao criar forma de pagamento:', err);
      setError('Erro ao criar forma de pagamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para deletar forma de pagamento
  const handleDeleteForma = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  // Confirmar exclusão
  const confirmDeleteForma = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);

      const response = await apiService.deleteFormaPagamento(deleteId);

      if (response.success) {
        closeModal('delete_forma');
        await fetchFormas();
        toast.success("Forma de pagamento excluída com sucesso!");
        setDeleteId(null);
      } else {
        setDeleteError('Erro ao excluir forma de pagamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao excluir forma de pagamento:', err);
      setDeleteError('Erro ao excluir forma de pagamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para definir como padrão
  const handleSetPadrao = async (id: number) => {
    try {
      const response = await apiService.setFormaPagamentoPadrao(id);
      
      if (response.success) {
        await fetchFormas();
        toast.success("Forma de pagamento definida como padrão!");
      } else {
        toast.error('Erro ao definir como padrão: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao definir como padrão:', err);
      toast.error('Erro ao definir como padrão: ' + (err.message || 'Erro desconhecido'));
    }
  };

  // Função para manipular mudanças no formulário
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'ativo' || name === 'padrao' 
        ? (value === '1' ? true : false) 
        : value
    });
  };

  // Função para manipular mudanças no formulário de edição
  const handleUpdateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUpdateFormData({
      ...updateFormData,
      [name]: name === 'ativo' || name === 'padrao' 
        ? (value === '1' ? true : false) 
        : value
    });
  };

  // Definir as colunas da tabela
  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      sorter: true,
      render: (text: string, record: FormaPagamento) => (
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column">
            <span className="fw-semibold">{record.nome || text}</span>
            {record.descricao && record.descricao.trim() !== '' && (
              <small className="text-muted">{record.descricao}</small>
            )}
          </div>
          {record.padrao && (
            <span className="badge bg-warning badge-sm ms-2">Padrão</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      sorter: true,
      render: (ativo: boolean) => (
        <span className={`badge badge-pill ${ativo ? 'bg-success' : 'bg-danger'}`}>
          {ativo ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      title: "Ações",
      dataIndex: "acoes",
      key: "acoes",
      render: (text: string, record: FormaPagamento) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvas_edit"
            onClick={() => handleEditForma(record.id)}
          >
            <i className="ti ti-edit"></i>
          </button>
          {!record.padrao && (
            <button
              className="btn btn-sm btn-outline-warning"
              onClick={() => handleSetPadrao(record.id)}
              title="Definir como padrão"
            >
              <i className="ti-star"></i>
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-danger"
            data-bs-toggle="modal"
            data-bs-target="#delete_forma"
            onClick={() => handleDeleteForma(record.id)}
          >
            <i className="ti ti-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Formas de Pagamento
                      <span className="count-title">{filteredFormas.length} formas</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Formas de Pagamento"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-plus me-2"></i>
                          Nova Forma de Pagamento
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Tabela de Formas de Pagamento */}
                  {isLoading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-2">Carregando formas de pagamento...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : !isSearching && searchText && filteredFormas.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="ti ti-search-off fs-3 text-muted"></i>
                      </div>
                      <h5 className="text-muted">Nenhuma forma de pagamento encontrada</h5>
                      <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                      <button
                        className="btn btn-outline-primary mt-2"
                        onClick={handleClearSearch}
                      >
                        <i className="ti ti-x me-1"></i>
                        Limpar pesquisa
                      </button>
                    </div>
                  ) : (
                    <ScrollableTable dataSource={filteredFormas} columns={columns} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Forma de Pagamento */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_add"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Nova Forma de Pagamento</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateForma}>
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nome"
                    value={formData.nome}
                    onChange={handleFormChange}
                    required
                    placeholder="Nome da forma de pagamento"
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    name="descricao"
                    value={formData.descricao || ''}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Descrição da forma de pagamento..."
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="ativo"
                    value={formData.ativo ? '1' : '0'}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="1">Ativo</option>
                    <option value="0">Inativo</option>
                  </select>
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="padrao"
                      checked={formData.padrao === true}
                      onChange={(e) => setFormData({...formData, padrao: e.target.checked})}
                    />
                    <label className="form-check-label">
                      Definir como padrão
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Criando...
                  </>
                ) : (
                  'Criar Forma de Pagamento'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Editar Forma de Pagamento */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_edit"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Editar Forma de Pagamento</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitEdit}>
              <div className="row">
                {editError && (
                  <div className="alert alert-danger mb-3" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {editError}
                  </div>
                )}
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Nome <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="nome"
                      value={updateFormData.nome || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Nome da forma de pagamento"
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Descrição
                    </label>
                    <textarea
                      className="form-control"
                      name="descricao"
                      value={updateFormData.descricao || ''}
                      onChange={handleUpdateFormChange}
                      rows={3}
                      placeholder="Descrição da forma de pagamento..."
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="ativo"
                      value={updateFormData.ativo ? '1' : '0'}
                      onChange={handleUpdateFormChange}
                      required
                    >
                      <option value="1">Ativo</option>
                      <option value="0">Inativo</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="padrao"
                        checked={updateFormData.padrao === true}
                        onChange={(e) => setUpdateFormData({...updateFormData, padrao: e.target.checked})}
                      />
                      <label className="form-check-label">
                        Definir como padrão
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Modal Confirmar Exclusão */}
      <div className="modal fade" id="delete_forma" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Excluir Forma de Pagamento?</h4>
                <p className="mb-0">
                  Tem certeza de que deseja excluir <br /> esta forma de pagamento?
                </p>

                {/* Exibir erro no modal se houver */}
                {deleteError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {deleteError}
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={() => setDeleteError(null)}
                  >
                    Cancelar
                  </Link>
                  <button
                    onClick={confirmDeleteForma}
                    className="btn btn-danger"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Excluir'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormasPagamento;